import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface BlogPost {
  id: string;
  title: string;
  summary: string;
  category: string;
  date: string;
  image: string;
  tags: string[];
}

const Blog: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Sample blog posts data
  const blogPosts: BlogPost[] = [
    {
      id: "cbam-impact",
      title: t("blog.posts.cbam.title") || "Impact du CBAM sur les entreprises exportatrices",
      summary: t("blog.posts.cbam.summary") || "Analyse des conséquences du mécanisme d'ajustement carbone aux frontières pour les exportateurs",
      category: "regulation",
      date: "2025-05-15",
      image: "/images/blog/cbam.jpg",
      tags: ["CBAM", "Exportation", "Réglementation"]
    },
    {
      id: "decarbonization-strategies",
      title: t("blog.posts.strategies.title") || "Stratégies de décarbonation efficaces pour les PME",
      summary: t("blog.posts.strategies.summary") || "Guide pratique pour mettre en œuvre des actions de réduction d'empreinte carbone adaptées aux PME",
      category: "strategy",
      date: "2025-05-10",
      image: "/images/blog/strategy.jpg",
      tags: ["PME", "Stratégie", "Décarbonation"]
    },
    {
      id: "climate-financing",
      title: t("blog.posts.financing.title") || "Financement des projets climat : opportunités et mécanismes",
      summary: t("blog.posts.financing.summary") || "Panorama des solutions de financement disponibles pour les projets de décarbonation",
      category: "financing",
      date: "2025-05-05",
      image: "/images/blog/financing.jpg",
      tags: ["Financement", "Investissement", "Projets climat"]
    },
    {
      id: "textile-case-study",
      title: t("blog.posts.case_study.title") || "Étude de cas : Transformation écologique d'une entreprise textile",
      summary: t("blog.posts.case_study.summary") || "Analyse d'un cas concret de décarbonation réussie dans le secteur textile",
      category: "case_study",
      date: "2025-04-28",
      image: "/images/blog/case-study.jpg",
      tags: ["Textile", "Étude de cas", "Transformation"]
    },
    {
      id: "carbon-innovations",
      title: t("blog.posts.innovation.title") || "Innovations dans la mesure et la réduction de l'empreinte carbone",
      summary: t("blog.posts.innovation.summary") || "Les dernières avancées technologiques au service de la décarbonation",
      category: "innovation",
      date: "2025-04-20",
      image: "/images/blog/innovation.jpg",
      tags: ["Innovation", "Technologie", "Mesure carbone"]
    },
    {
      id: "carbon-market-trends",
      title: t("blog.posts.market.title") || "Tendances du marché carbone : opportunités et défis",
      summary: t("blog.posts.market.summary") || "Analyse des évolutions récentes du marché carbone et perspectives pour les entreprises",
      category: "market",
      date: "2025-04-15",
      image: "/images/blog/market.jpg",
      tags: ["Marché carbone", "Tendances", "Opportunités"]
    }
  ];
  
  // Filter posts by category and search term
  const filteredPosts = blogPosts.filter(post => 
    (activeCategory === "all" || post.category === activeCategory) &&
    (searchTerm === "" || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );
  
  // Get all unique tags from posts
  const allTags = Array.from(new Set(blogPosts.flatMap(post => post.tags)));
  
  // Format date to localized string
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-mypa-blue mb-4">{t("blog.title") || "Blog"}</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t("blog.description") || "Actualités, analyses et conseils sur la décarbonation"}</p>
      </div>
      
      {/* Search and categories */}
      <div className="mb-12">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8">
          {/* Categories */}
          <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4 md:mb-0">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === "all"
                  ? "bg-mypa-blue text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("blog.categories.all") || "Tous les articles"}
            </button>
            <button
              onClick={() => setActiveCategory("regulation")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === "regulation"
                  ? "bg-mypa-blue text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("blog.categories.regulation") || "Réglementation"}
            </button>
            <button
              onClick={() => setActiveCategory("strategy")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === "strategy"
                  ? "bg-mypa-blue text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("blog.categories.strategy") || "Stratégie"}
            </button>
            <button
              onClick={() => setActiveCategory("financing")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === "financing"
                  ? "bg-mypa-blue text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("blog.categories.financing") || "Financement"}
            </button>
            <button
              onClick={() => setActiveCategory("case_study")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === "case_study"
                  ? "bg-mypa-blue text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("blog.categories.case_study") || "Études de cas"}
            </button>
            <button
              onClick={() => setActiveCategory("innovation")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === "innovation"
                  ? "bg-mypa-blue text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("blog.categories.innovation") || "Innovation"}
            </button>
            <button
              onClick={() => setActiveCategory("market")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === "market"
                  ? "bg-mypa-blue text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t("blog.categories.market") || "Marché"}
            </button>
          </div>
          
          {/* Search */}
          <div className="w-full md:w-auto">
            <div className="relative">
              <input
                type="text"
                placeholder={t("blog.search") || "Rechercher un article"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Blog posts grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        {filteredPosts.length > 0 ? (
          filteredPosts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="h-48 bg-gray-200">
                {/* Placeholder for post image */}
                <div className="w-full h-full flex items-center justify-center bg-mypa-blue bg-opacity-10">
                  <span className="text-mypa-blue font-medium">{post.category}</span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <span>{formatDate(post.date)}</span>
                  <span className="mx-2">•</span>
                  <span className="capitalize">{t(`blog.categories.${post.category}`) || post.category}</span>
                </div>
                <h3 className="text-xl font-bold text-mypa-blue mb-2">{post.title}</h3>
                <p className="text-gray-600 mb-4">{post.summary}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.map((tag, index) => (
                    <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/blog/${post.id}`}
                  className="text-mypa-green font-medium hover:underline"
                >
                  {t("blog.read_more") || "Lire la suite"} →
                </Link>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500 text-lg">Aucun article ne correspond à votre recherche.</p>
          </div>
        )}
      </div>
      
      {/* Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent posts */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("blog.recent_posts") || "Articles récents"}</h3>
          <div className="space-y-4">
            {blogPosts.slice(0, 3).map((post) => (
              <div key={post.id} className="flex items-start">
                <div className="w-16 h-16 bg-gray-200 rounded flex-shrink-0 mr-4">
                  {/* Placeholder for post thumbnail */}
                  <div className="w-full h-full flex items-center justify-center bg-mypa-blue bg-opacity-10">
                    <span className="text-xs text-mypa-blue">{post.category}</span>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-mypa-blue mb-1">{post.title}</h4>
                  <p className="text-sm text-gray-500">{formatDate(post.date)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Popular tags */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("blog.popular_tags") || "Tags populaires"}</h3>
          <div className="flex flex-wrap gap-2">
            {allTags.map((tag, index) => (
              <button
                key={index}
                onClick={() => setSearchTerm(tag)}
                className="text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-gray-200"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        
        {/* Newsletter */}
        <div className="bg-mypa-blue bg-opacity-10 rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-mypa-blue mb-2">{t("blog.newsletter.title") || "Abonnez-vous à notre newsletter"}</h3>
          <p className="text-gray-600 mb-4">{t("blog.newsletter.description") || "Recevez nos derniers articles et actualités directement dans votre boîte mail"}</p>
          <form className="flex flex-col space-y-3">
            <input
              type="email"
              placeholder={t("blog.newsletter.placeholder") || "Votre adresse email"}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
            />
            <button
              type="submit"
              className="bg-mypa-green text-white px-4 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
            >
              {t("blog.newsletter.button") || "S'abonner"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Blog;
